import { useEffect, useId, useState } from 'react';
import { Loader, Save } from 'lucide-react';
import { partnersApi } from '../../services/partnersApi';
import { PARTNER_REPORT_STAFF_STATUSES, type PartnerReportStaffStatus, type ReportView } from '../../types/partners';
import { cn } from '../../utils/cn';
import { reportStatusLabel } from '../../utils/partnerStatus';
import { describeReportOutcome, type PartnerNotice } from '../../utils/partnerSync';

interface PartnerUpdateStatusControlProps {
  report: ReportView;
  onSaved: (report: ReportView) => void;
  onNotice: (notice: PartnerNotice) => void;
  /**
   * `compact`: a status select that saves as soon as it changes, for triage in
   * a list. The full control adds the note and whether the partner sees it.
   */
  compact?: boolean;
  disabled?: boolean;
  className?: string;
}

const isStaffStatus = (status: string): status is PartnerReportStaffStatus =>
  (PARTNER_REPORT_STAFF_STATUSES as string[]).includes(status);

const savedSentence = (report: ReportView, status: PartnerReportStaffStatus) =>
  `${report.pack_name} ${report.version} is now ${reportStatusLabel(status).label.toLowerCase()}.`;

/** Move a report on: acknowledged, scheduled, live or dismissed, with an optional note for the partner. */
export const PartnerUpdateStatusControl = ({ report, onSaved, onNotice, compact = false, disabled = false, className }: PartnerUpdateStatusControlProps) => {
  const uid = useId();
  const current = isStaffStatus(report.status) ? report.status : '';
  const [status, setStatus] = useState<PartnerReportStaffStatus | ''>(current);
  const [note, setNote] = useState(report.status_note ?? '');
  const [notePublic, setNotePublic] = useState(report.status_note_public);
  const [saving, setSaving] = useState(false);

  // Follow the report when it changes underneath (a poll, or another row's save).
  useEffect(() => {
    setStatus(isStaffStatus(report.status) ? report.status : '');
    setNote(report.status_note ?? '');
    setNotePublic(report.status_note_public);
  }, [report.id, report.revision, report.status, report.status_note, report.status_note_public]);

  const save = async (next: { status?: PartnerReportStaffStatus; status_note?: string | null; status_note_public?: boolean }) => {
    setSaving(true);
    try {
      const res = await partnersApi.updateReport(report.id, next);
      onSaved(res.report);
      onNotice(describeReportOutcome(res.discord, next.status ? savedSentence(res.report, next.status) : `Saved the note on ${report.pack_name} ${report.version}.`));
    } catch (error) {
      onNotice({ tone: 'danger', text: error instanceof Error ? error.message : 'The report could not be updated.' });
      setStatus(current);
    } finally {
      setSaving(false);
    }
  };

  const options = (
    <>
      {!current && (
        <option value="" disabled>
          {reportStatusLabel(report.status).label}
        </option>
      )}
      {PARTNER_REPORT_STAFF_STATUSES.map((s) => (
        <option key={s} value={s}>
          {reportStatusLabel(s).label}
        </option>
      ))}
    </>
  );

  if (compact) {
    return (
      <span className={cn('inline-flex items-center gap-1.5', className)}>
        <select
          className="glass-select min-h-[32px] py-1 text-sm"
          value={status}
          disabled={disabled || saving}
          onChange={(e) => {
            const next = e.target.value as PartnerReportStaffStatus;
            setStatus(next);
            void save({ status: next });
          }}
          aria-label={`Status of ${report.pack_name} ${report.version}`}
          onClick={(e) => e.stopPropagation()}
        >
          {options}
        </select>
        {saving && <Loader className="w-4 h-4 animate-spin text-glass-400" />}
      </span>
    );
  }

  const dirty = status !== current || (note.trim() || null) !== (report.status_note ?? null) || notePublic !== report.status_note_public;

  return (
    <div className={cn('space-y-3', className)}>
      <div className="grid gap-3 sm:grid-cols-[12rem_minmax(0,1fr)]">
        <div>
          <label htmlFor={`${uid}-status`} className="block text-xs font-medium text-glass-400 mb-1.5">
            Status
          </label>
          <select
            id={`${uid}-status`}
            className="glass-select w-full"
            value={status}
            disabled={disabled || saving}
            onChange={(e) => setStatus(e.target.value as PartnerReportStaffStatus)}
          >
            {options}
          </select>
          <p className="text-xs text-glass-500 mt-1">{status ? reportStatusLabel(status).hint : 'Choose where this report has got to.'}</p>
        </div>
        <div>
          <label htmlFor={`${uid}-note`} className="block text-xs font-medium text-glass-400 mb-1.5">
            Note (optional)
          </label>
          <textarea
            id={`${uid}-note`}
            className="glass-input w-full min-h-[72px] resize-y"
            maxLength={500}
            value={note}
            disabled={disabled || saving}
            placeholder={status === 'scheduled' ? 'e.g. Going out with Thursday’s restart' : status === 'dismissed' ? 'e.g. We are staying on 2.3 for this season' : 'Anything worth saying about this report'}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>
      </div>
      {report.partner_visible ? (
        <label className="flex items-start justify-between gap-4">
          <span className="min-w-0">
            <span className="block text-sm text-white">Show this note to the partner</span>
            <span className="block text-xs text-glass-400">The partner always sees the status on the report’s card in their channel. The note shows there only if this is on.</span>
          </span>
          <span className="switch shrink-0">
            <input type="checkbox" checked={notePublic} disabled={disabled || saving} onChange={(e) => setNotePublic(e.target.checked)} aria-label="Show this note to the partner" />
            <span />
          </span>
        </label>
      ) : (
        <p className="text-xs text-glass-500">Only staff see this report, so the note stays with staff.</p>
      )}
      <div className="flex justify-end">
        <button
          type="button"
          className="btn-primary"
          disabled={disabled || saving || !status || !dirty}
          onClick={() =>
            void save({
              ...(status && status !== current ? { status } : {}),
              status_note: note.trim() || null,
              status_note_public: report.partner_visible ? notePublic : false,
            })
          }
        >
          {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save
        </button>
      </div>
    </div>
  );
};
